import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet } from 'react-router-dom'

import { LanguageSwitcher } from '@/shared/ui'
import AppFooter from '@/shared/ui/AppFooter'
import { Logo } from '@/shared/ui/brand'

function MenuIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

function CloseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

/**
 * Header horizontal de las landings de Safyro (Contadores, Fintrack): logo a
 * la izquierda, navegación por anclas al centro, login + CTA a la derecha.
 * A diferencia de `PublicLayout` (sidebar oscuro, para navegar la app ya
 * dentro), esta es la puerta de venta: nadie que llega a "/" tiene sesión
 * todavía, así que no hace falta el shell de la app.
 */
export default function MarketingLayout() {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)

  const navLinks = [
    { href: '#producto', label: t('marketing.nav.product') },
    { href: '#precios', label: t('marketing.nav.pricing') },
    { href: '#preguntas', label: t('marketing.nav.faq') },
  ]

  return (
    <div className="flex min-h-full flex-col bg-white">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2"
      >
        {t('marketing.skipToContent')}
      </a>

      <header className="sticky top-0 z-40 border-b border-ink-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" aria-label={t('app.name')} className="shrink-0">
            <Logo className="h-8" />
          </Link>

          <nav
            aria-label={t('marketing.nav.label')}
            className="hidden items-center gap-8 md:flex"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-ink-600 transition hover:text-ink-900"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden shrink-0 items-center gap-3 md:flex">
            <LanguageSwitcher />
            <Link
              to="/login"
              className="text-sm font-semibold text-ink-700 transition hover:text-ink-900"
            >
              {t('marketing.nav.login')}
            </Link>
            <Link
              to="/login?modo=registro"
              className="inline-flex items-center justify-center rounded-full bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(11,61,180,.28)] transition hover:brightness-110"
            >
              {t('marketing.nav.cta')}
            </Link>
          </div>

          <button
            type="button"
            aria-label={t('nav.menu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="grid size-9 shrink-0 place-items-center rounded-lg text-ink-700 transition hover:bg-ink-50 md:hidden"
          >
            {menuOpen ? <CloseIcon className="size-6" /> : <MenuIcon className="size-6" />}
          </button>
        </div>

        {menuOpen ? (
          <nav
            aria-label={t('marketing.nav.label')}
            className="border-t border-ink-100 bg-white px-4 py-3 sm:px-6 md:hidden"
          >
            <div className="mx-auto flex max-w-7xl flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-xl px-3 py-2.5 text-sm font-medium text-ink-600 transition hover:bg-ink-50 hover:text-ink-900"
                >
                  {link.label}
                </a>
              ))}
              <div className="mt-2 flex items-center justify-between gap-3 border-t border-ink-100 pt-3">
                <LanguageSwitcher />
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-semibold text-ink-700"
                >
                  {t('marketing.nav.login')}
                </Link>
              </div>
              <Link
                to="/login?modo=registro"
                onClick={() => setMenuOpen(false)}
                className="mt-2 inline-flex items-center justify-center rounded-full bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white"
              >
                {t('marketing.nav.cta')}
              </Link>
            </div>
          </nav>
        ) : null}
      </header>

      <main id="contenido" className="flex-1">
        <Outlet />
      </main>

      <AppFooter />
    </div>
  )
}
